import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ShoppingBag, Minus, Plus, ArrowLeft, Check } from "lucide-react";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
    } catch (error) {
      console.error("Failed to fetch product:", error);
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.error("Please sign in to add items to cart");
      navigate("/login");
      return;
    }
    const success = await addToCart(productId, quantity);
    if (success) {
      setQuantity(1);
    }
  };

  if (loading) {
    return (
      <div className="container-main section-spacing">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-32 mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="aspect-square bg-muted rounded-lg" />
            <div>
              <div className="h-10 bg-muted rounded w-3/4 mb-4" />
              <div className="h-6 bg-muted rounded w-1/4 mb-8" />
              <div className="h-4 bg-muted rounded w-full mb-2" />
              <div className="h-4 bg-muted rounded w-full mb-2" />
              <div className="h-4 bg-muted rounded w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container-main section-spacing text-center">
        <h1 className="text-h2 mb-4">Product Not Found</h1>
        <Button onClick={() => navigate("/")} className="btn-primary">
          Back to Home
        </Button>
      </div>
    );
  }

  const benefits = product.id === "mud-bath" 
    ? ["Deep skin detoxification", "Rich in natural minerals", "Relaxing spa experience", "Suitable for all skin types"]
    : ["Gentle daily cleansing", "Natural clay formula", "Nourishing oils", "Eco-friendly packaging"];

  return (
    <div className="container-main section-spacing">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
        data-testid="back-button"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
        {/* Product Image */}
        <div className="aspect-square overflow-hidden rounded-lg bg-muted">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Product Details */}
        <div className="flex flex-col">
          <p className="text-caption text-primary mb-2">{product.category}</p>
          <h1 className="text-h2 mb-4" data-testid="product-name">{product.name}</h1>
          <p className="text-3xl font-heading text-primary mb-6" data-testid="product-price">
            ${product.price.toFixed(2)}
          </p>
          
          <p className="text-body-lg text-muted-foreground mb-8">
            {product.description}
          </p>

          {/* Benefits */}
          <div className="mb-8">
            <h3 className="font-heading text-lg mb-4">Benefits</h3>
            <ul className="space-y-3">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-secondary flex items-center justify-center">
                    <Check className="w-3 h-3 text-primary" />
                  </div>
                  <span className="text-muted-foreground">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Quantity Selector */}
          <div className="flex items-center gap-4 mb-8">
            <span className="font-medium">Quantity:</span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
                data-testid="quantity-decrease"
              >
                <Minus className="w-4 h-4" />
              </Button>
              <span className="w-12 text-center font-medium" data-testid="quantity-value">
                {quantity}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setQuantity(quantity + 1)}
                data-testid="quantity-increase"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Add to Cart */}
          <Button 
            className="btn-primary w-full md:w-auto"
            onClick={handleAddToCart}
            data-testid="add-to-cart-btn"
          >
            <ShoppingBag className="w-5 h-5 mr-2" />
            Add to Cart - ${(product.price * quantity).toFixed(2)}
          </Button>
        </div>
      </div>
    </div>
  );
}
